import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import {
  fixTimezoneOffsetYYYYMMDD,
  WeekendHighlightColor,
} from '../../constants/constants';
import WeekdaysEnum from '../../constants/enums/weekend-enum';
import URLConstants from '../../constants/url-constants';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { IAddHoliday } from '../../models/forms/IAddEditHoliday';
import IAddEditWeekendHolidays from '../../models/forms/IAddEditWeekendHoliday';
import { IAddProcess } from '../../models/forms/IAddUpdateProcess';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IHolidayList,
  IHolidayListVM,
  IHolidayVM,
  IWeekendList,
} from '../../models/response/IHolidayResponse';
import { IFactoryScheduleState } from '../../models/state/IFactoryScheduleState';
import { formatMessage } from '../../translations/format-message';
import {
  AddEditHolidayInitialState,
  GetAllHolidayState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';

export class FactoryScheduleStore implements IFactoryScheduleState {
  initialStateValue = ObservableInitialState;
  addEditHolidayInitalState = AddEditHolidayInitialState;
  factoryScheduleList: IHolidayListVM = GetAllHolidayState;
  factorySchedule: IHolidayVM | undefined = undefined;
  getFactoryScheduleByIdState = { ...this.initialStateValue };
  addUpdateFactoryScheduleState = { ...this.initialStateValue };
  deleteFactoryScheduleState = { ...this.initialStateValue };
  getAllFactoryScheduleState = { ...this.initialStateValue };
  addEditFactoryWeekendState = { ...this.initialStateValue };
  fileUploadState = { ...this.initialStateValue };
  exportFileState = { ...this.initialStateValue };
  base64String: any = { fileString: '' };
  deleteAllFactoryScheduleState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      factorySchedule: observable,
      getFactoryScheduleByIdState: observable,
      factoryScheduleList: observable,
      addUpdateFactoryScheduleState: observable,
      deleteFactoryScheduleState: observable,
      getAllFactoryScheduleState: observable,
      GetFactoryScheduleListService: action,
      GetFactoryScheduleByIdService: action,
      AddFactoryScheduleService: action,
      UpdateFactoryScheduleService: action,
      DeleteFactoryScheduleService: action,
      resetDeleteFactoryScheduleState: action,
      resetAddUpdateFactoryScheduleState: action,
      reset: action,
      allFactorySchedule: computed,
      factoryScheduleDetails: computed,
      AddFactoryWeekendService: action,
      addEditFactoryWeekendState: observable,
      allFactoryWeekendSchedule: computed,
      resetAddEditFactoryWeekendState: action,
      holidaysFileUploadService: action,
      fileUploadState: observable,
      resetFileUploadState: action,
      ExportFileService: action,
      exportFileState: observable,
      resetExportFileState: action,
      base64String: observable,
      calendarHolidays: computed,
      WeekendSchedule: computed,
      weekendDays: computed,
      DeleteAllFactoryScheduleService: action,
      deleteAllFactoryScheduleState: observable,
      resetDeleteAllFactoryScheduleState: action,
    });
  }

  //#region Actions
  GetFactoryScheduleListService = (TenantId: number, Year: number) => {
    this.getAllFactoryScheduleState.inProgress = true;
    const url = `${URLConstants.GetAllScheduleList}?TenantId=${TenantId}&Year=${Year}`;

    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IHolidayListVM>>) => {
        if (response.data.Error) {
          this.getAllFactoryScheduleState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.factoryScheduleList = response.data.Data;
          this.getAllFactoryScheduleState.success = true;
        }
      })
      .catch((err: string) => {
        this.getAllFactoryScheduleState.error = err;
      })
      .finally(
        action(() => {
          this.getAllFactoryScheduleState.inProgress = false;
        })
      );
  };

  AddFactoryScheduleService = (data: IAddHoliday) => {
    this.addUpdateFactoryScheduleState.inProgress = true;
    const url = URLConstants.AddHoliday;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateFactoryScheduleState.error = response.data.Message;
        } else {
          this.addUpdateFactoryScheduleState.success = true;
        }
      })
      .catch((err: string) => {
        // this.addUpdateFactoryScheduleState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateFactoryScheduleState.inProgress = false;
        })
      );
  };

  AddFactoryWeekendService = (data: IAddEditWeekendHolidays) => {
    this.addEditFactoryWeekendState.inProgress = true;
    const url = URLConstants.AddWeekendHoliday;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addEditFactoryWeekendState.error = response.data.Message;
        } else {
          this.addEditFactoryWeekendState.success = true;
        }
      })
      .catch((err: string) => {
        this.addEditFactoryWeekendState.error = err;
      })
      .finally(
        action(() => {
          this.addEditFactoryWeekendState.inProgress = false;
        })
      );
  };

  UpdateFactoryScheduleService = (data: IAddProcess) => {
    this.addUpdateFactoryScheduleState.inProgress = true;
    let url = URLConstants.UpdateProcess;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateFactoryScheduleState.error = response.data.Message;
        } else {
          this.addUpdateFactoryScheduleState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateFactoryScheduleState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateFactoryScheduleState.inProgress = false;
        })
      );
  };

  GetFactoryScheduleByIdService = (TenantId: number, HolidayId: number) => {
    this.getFactoryScheduleByIdState.inProgress = true;
    let url =
      URLConstants.GetFactoryScheduleById +
      '/' +
      TenantId +
      '?HolidayId=' +
      HolidayId;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IHolidayVM>>) => {
        if (response.data.Error) {
          this.getFactoryScheduleByIdState.error = response.data.Message;
        } else {
          this.factorySchedule = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getFactoryScheduleByIdState.error = err;
      })
      .finally(
        action(() => {
          this.getFactoryScheduleByIdState.inProgress = false;
        })
      );
  };

  DeleteFactoryScheduleService = (
    TenantId: number,
    SchecduleId: number,
    year: number
  ) => {
    this.deleteFactoryScheduleState.inProgress = true;
    let url = `${URLConstants.DeleteHoliday}/${TenantId}?SchecduleId=${SchecduleId}&Year=${year}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteFactoryScheduleState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteFactoryScheduleState.success = true;
      })
      .catch((err: string) => {
        this.deleteFactoryScheduleState.error = err;
      })
      .finally(
        action(() => {
          this.deleteFactoryScheduleState.inProgress = false;
        })
      );
  };

  holidaysFileUploadService = (TenantId: number, file: File) => {
    this.fileUploadState.inProgress = true;
    const url = `${URLConstants.UploadHolidaysFile}/${TenantId}`;
    const formData = new FormData();
    formData.append('file', file);
    return baseService
      .postMultipart(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.fileUploadState.error = response.data.Message;
        } else {
          this.fileUploadState.success = true;
        }
      })
      .catch((err: string) => {
        this.fileUploadState.error = formatMessage('SomethingWentWrong');
        console.error(err);
      })
      .finally(
        action(() => {
          this.fileUploadState.inProgress = false;
        })
      );
  };

  ExportFileService = (TenantId: number, Year: number) => {
    this.exportFileState.inProgress = true;
    let url = `${URLConstants.ExportFile}/${TenantId}?Year=${Year}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.exportFileState.error = response.data.Message;
        } else {
          this.exportFileState.success = true;
          this.base64String = { fileString: response.data.Data };
        }
      })
      .catch((err: string) => {
        this.exportFileState.error = err;
      })
      .finally(
        action(() => {
          this.exportFileState.inProgress = false;
        })
      );
  };

  DeleteAllFactoryScheduleService = (TenantId: number, year: number) => {
    this.deleteAllFactoryScheduleState.inProgress = true;
    let url = `${URLConstants.DeleteAllHoliday}/${TenantId}?Year=${year}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteAllFactoryScheduleState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.deleteAllFactoryScheduleState.success = true;
        }
      })
      .catch((err: string) => {
        this.deleteAllFactoryScheduleState.error = err;
      })
      .finally(
        action(() => {
          this.deleteAllFactoryScheduleState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allFactorySchedule(): IHolidayList[] {
    let holidayData: IHolidayList[] = [];
    if (
      this.factoryScheduleList?.Schedules &&
      this.factoryScheduleList.Schedules?.length > 0
    ) {
      this.factoryScheduleList.Schedules.map((holiday) => {
        if (holiday.HolidayId >= 0) {
          holidayData.push({
            ...holiday,
            Day: formatMessage(holiday.Day),
            HolidayName: holiday.HolidayName || 'N/A',
            Comment: holiday.Comment || 'N/A',
            HolidayDate: fixTimezoneOffsetYYYYMMDD(holiday.HolidayDate) || '-',
            isDelete: holiday.Year >= new Date().getFullYear(),
            // isDelete: HolidayId > 0 && Year >= new Date().getFullYear(),
          });
        }
      });
    }
    return holidayData;
  }

  get calendarHolidays(): any {
    let holidayData: any = {};
    if (
      this.factoryScheduleList?.Schedules &&
      this.factoryScheduleList.Schedules?.length > 0
    ) {
      this.factoryScheduleList.Schedules.map(
        ({ HolidayId, HolidayDate, HolidayName }) => {
          if (HolidayId >= 0) {
            holidayData = {
              ...holidayData,
              [HolidayDate]: {
                label: HolidayName,
                color: WeekendHighlightColor,
                holidayId: HolidayId,
              },
            };
          }
        }
      );
    }
    return holidayData;
  }

  get weekendDays(): number[] {
    let weekendData: number[] = [];
    if (
      this.factoryScheduleList?.Schedules &&
      this.factoryScheduleList.Schedules?.length > 0
    ) {
      this.factoryScheduleList.Schedules.map(({ HolidayId, Day }) => {
        if (HolidayId < 0) {
          weekendData.push(WeekdaysEnum[Day]);
        }
      });
    }
    return weekendData;
  }

  get WeekendSchedule(): IAddEditWeekendHolidays {
    if (
      this.factoryScheduleList?.Schedules &&
      this.factoryScheduleList.Schedules?.length > 0
    ) {
      const negativeHolidayIdSchedules =
        this.factoryScheduleList?.Schedules?.filter((s) => s.HolidayId < 0);

      if (negativeHolidayIdSchedules && negativeHolidayIdSchedules.length > 0) {
        return {
          Year: negativeHolidayIdSchedules[0].Year,
          Weekend_1: negativeHolidayIdSchedules[0]?.Day
            ? WeekdaysEnum[negativeHolidayIdSchedules[0].Day]
            : -1,
          Weekend_2: negativeHolidayIdSchedules[1]?.Day
            ? WeekdaysEnum[negativeHolidayIdSchedules[1].Day]
            : -1,
          Comment: 'Weekend',
        };
      }
    }
    return {
      Year: 2025,
      Weekend_1: -1,
      Weekend_2: -1,
      Comment: 'Weekend',
    };
  }

  get allFactoryWeekendSchedule(): IWeekendList[] {
    let holidayData: IWeekendList[] = [];
    if (
      this.factoryScheduleList?.Schedules &&
      this.factoryScheduleList.Schedules?.length > 0
    ) {
      this.factoryScheduleList.Schedules.map(({ HolidayId, Day }) => {
        if (HolidayId < 0) {
          holidayData.push({
            Day,
          });
        }
      });
      return holidayData;
    } else {
      return [{ Day: 'None' }, { Day: 'None' }];
    }
  }

  get factoryScheduleDetails(): IAddHoliday {
    if (this.factorySchedule) {
      return {
        HolidayDate:
          fixTimezoneOffsetYYYYMMDD(this.factorySchedule.HolidayDate) || '-',
        Comment: this.factorySchedule.Comment,
        HolidayName: this.factorySchedule.HolidayName || 'N/A',
        TenantId: Number(getTenantIdFromLocalStorage()),
        Year: this.factorySchedule.Year,
      };
    }
    return this.addEditHolidayInitalState;
  }

  //#endregion

  //#region Reset
  reset = () => {
    this.factoryScheduleList = GetAllHolidayState;
  };

  resetAddEditFactoryWeekendState = () => {
    this.addEditFactoryWeekendState = { ...this.initialStateValue };
  };

  resetFactoryScheduleDetail = () => {
    this.factorySchedule = undefined;
    this.getFactoryScheduleByIdState = { ...this.initialStateValue };
  };

  resetDeleteFactoryScheduleState = () => {
    this.deleteFactoryScheduleState = { ...this.initialStateValue };
  };

  resetDeleteAllFactoryScheduleState = () => {
    this.deleteAllFactoryScheduleState = { ...this.initialStateValue };
  };

  resetAddUpdateFactoryScheduleState = () => {
    this.addUpdateFactoryScheduleState.inProgress = false;
    this.addUpdateFactoryScheduleState.success = false;
    this.addUpdateFactoryScheduleState.error = '';
  };

  resetFileUploadState = () => {
    this.fileUploadState.inProgress = false;
    this.fileUploadState.success = false;
    this.fileUploadState.error = '';
  };

  resetExportFileState = () => {
    this.exportFileState = { ...this.initialStateValue };
  };

  //#endregion
}

export default new FactoryScheduleStore();
